/**
 * platformToolLabels.ts
 *
 * Pure-data, client-safe tool label resolution.
 * Does NOT import or reference platformToolsList or anything
 * that would cause server-only code to be bundled client-side.
 *
 * Uses:
 * - PLATFORM_TOOL_LABELS (pure string map for platform tools)
 * - NEXUS_TOOL_LOADER_LABELS (for Nexus/CRM)
 */

import { PLATFORM_TOOL_LABELS } from './platformToolLabelsData'
import { NEXUS_TOOL_LOADER_LABELS } from '@/lib/sidekick/nexus-annotation-keys'
import { getRoutedLosiSpacesToolKey } from '@/lib/spaces/losi-spaces-tool-routing'

type AnyRecord = Record<string, unknown>

// --------------
// Return a short, human-friendly label suitable for compact timeline rows.
//
// This file contains only client-bundle-safe logic—no platformToolsList import!
export function getPlatformToolDisplayLabel(toolName: string): string {
  const trimmed = toolName.trim()
  if (NEXUS_TOOL_LOADER_LABELS[trimmed]) {
    return NEXUS_TOOL_LOADER_LABELS[trimmed].replace(/\s*\u2026\s*$/, '')
  }
  if (PLATFORM_TOOL_LABELS[trimmed]) {
    return PLATFORM_TOOL_LABELS[trimmed]
  }
  // Fallback: title-case
  return trimmed
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
}

// --------------
function coerceRecord(value: unknown): AnyRecord | null {
  if (value == null) return null
  if (Object.prototype.toString.call(value) !== '[object Object]') return null
  return value as AnyRecord
}

// --------------
function getString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

// --------------
function getDisplayTitle(args: unknown): string | null {
  const record = coerceRecord(args)
  if (!record) return null

  const displayTitle = getString(record.displayTitle)
  if (displayTitle) return displayTitle

  return null
}

// --------------
function getToolQueryTitle(args: unknown): string | null {
  const record = coerceRecord(args)
  if (!record) return null

  const directKeys = [
    'query',
    'q',
    'objective',
    'prompt',
    'input',
    'url',
    'location',
    'searchTerm',
    'term',
    'text',
    'topic',
    'question',
    'title',
    'name',
    'email',
    'conversationTitle',
    'spaceName',
    'serverName',
    'server_name',
    'toolName',
    'tool_name',
    'skillName',
    'skill_name',
    'integrationId',
    'integration_id',
    'skillId',
    'skill_id'
  ] as const

  for (const key of directKeys) {
    const value = getString(record[key])
    if (value) return value
  }

  const parametersRaw = record.parameters
  const parameters = coerceRecord(parametersRaw)
  if (parameters) {
    for (const key of directKeys) {
      const value = getString(parameters[key])
      if (value) return value
    }
  }

  return null
}

// --------------
function getToolMode(args: unknown): string | null {
  const record = coerceRecord(args)
  if (!record) return null

  const directMode = getString(record.mode)
  if (directMode) return directMode

  const parameters = coerceRecord(record.parameters)
  return parameters ? getString(parameters.mode) : null
}

// --------------
function truncateMiddle(value: string, maxLen: number): string {
  if (value.length <= maxLen) return value
  if (maxLen < 10) return value.slice(0, maxLen)
  const head = Math.ceil((maxLen - 1) * 0.6)
  const tail = Math.floor((maxLen - 1) * 0.4)
  return value.slice(0, head) + '\u2026' + value.slice(value.length - tail)
}

// --------------
/**
 * Return an action-style label suitable for timeline headers/subtitles, including
 * context if args/request objects are passed (e.g. "Searched “cats”").
 *
 * Note: Timeline rows may be visually truncated via CSS. We still ensure the
 * string we return contains the full (or safely middle-truncated) query/title.
 */
export function getPlatformToolActionLabel(toolName: string, args?: unknown): string {
  const key = toolName.trim()

  // --- 1. Nexus/CRM loader label
  if (NEXUS_TOOL_LOADER_LABELS[key]) {
    return NEXUS_TOOL_LOADER_LABELS[key].replace(/\s*\u2026\s*$/, '')
  }

  const serverTitle = getDisplayTitle(args)
  const title = serverTitle ?? getToolQueryTitle(args)

  if (key.startsWith('losiSpaces__')) {
    const routedKey = getRoutedLosiSpacesToolKey(key, args)
    const baseKey = routedKey ?? key
    const baseLabel = PLATFORM_TOOL_LABELS[baseKey] ?? getPlatformToolDisplayLabel(baseKey)
    return title ? `${baseLabel} “${truncateMiddle(title, 120)}”` : baseLabel
  }

  if (key.startsWith('ContentEngine__')) {
    const baseLabel = PLATFORM_TOOL_LABELS[key] ?? getPlatformToolDisplayLabel(key)
    return title ? `${baseLabel} “${truncateMiddle(title, 120)}”` : baseLabel
  }

  if (key.startsWith('MCPIntegrationTool__')) {
    const record = coerceRecord(args)
    const parameters = coerceRecord(record?.parameters)
    const source = parameters ?? record
    const serverName =
      getString(source?.serverName) ??
      getString(source?.server_name) ??
      getString(source?.query) ??
      getString(source?.integrationId) ??
      getString(source?.integration_id)
    const toolName = getString(source?.toolName) ?? getString(source?.tool_name)

    if (key.endsWith('__searchMcpServers')) {
      return serverName ? `Search MCP servers: “${truncateMiddle(serverName, 120)}”` : 'Search MCP servers'
    }

    if (key.endsWith('__installMcpServer')) {
      return serverName ? `Add MCP server “${truncateMiddle(serverName, 120)}”` : 'Add MCP server'
    }

    if (key.endsWith('__listMcpServerTools')) {
      return serverName ? `List MCP tools for “${truncateMiddle(serverName, 120)}”` : 'List MCP tools'
    }

    if (key.endsWith('__callMcpServerTool')) {
      const context = [serverName, toolName].filter(Boolean).join(' / ')
      return context ? `Run MCP action “${truncateMiddle(context, 120)}”` : 'Run MCP action'
    }
  }

  if (key.startsWith('SkillIntegrationTool__')) {
    const record = coerceRecord(args)
    const parameters = coerceRecord(record?.parameters)
    const source = parameters ?? record
    const skillName =
      getString(source?.skillName) ??
      getString(source?.skill_name) ??
      getString(source?.query) ??
      getString(source?.skillId) ??
      getString(source?.skill_id)

    if (key.endsWith('__searchSkills')) {
      return skillName ? `Search skills: “${truncateMiddle(skillName, 120)}”` : 'Search skills'
    }

    if (key.endsWith('__installSkill')) {
      return skillName ? `Install skill “${truncateMiddle(skillName, 120)}”` : 'Install skill'
    }

    if (key.endsWith('__createSkill')) {
      return skillName ? `Create skill “${truncateMiddle(skillName, 120)}”` : 'Create skill'
    }

    if (key.endsWith('__updateSkill')) {
      return skillName ? `Update skill “${truncateMiddle(skillName, 120)}”` : 'Update skill'
    }

    if (key.endsWith('__enableSkill')) {
      return skillName ? `Enable skill “${truncateMiddle(skillName, 120)}”` : 'Enable skill'
    }

    if (key.endsWith('__runSkill') || key.endsWith('__getSkillInstructions')) {
      return skillName ? `Load skill “${truncateMiddle(skillName, 120)}”` : 'Load skill'
    }
  }

  // --- 2. Contextual action label cases
  // Web search
  if (/googleSearch/i.test(key)) {
    if (title) return `Searched “${title}”`
    return 'Web search'
  }

  // Weather
  if (/getWeather/i.test(key)) {
    if (title) return `Weather for “${title}”`
    return 'Weather'
  }

  // Fetch URL
  if (/FetchDataFromUrl/i.test(key)) {
    if (title) return `Fetched “${truncateMiddle(title, 140)}”`
    return 'Fetch URL'
  }

  if (key === 'audioTool__processAudio') {
    const mode = getToolMode(args)
    if (mode === 'generate_voiceover') {
      return title ? `Generated voiceover: “${truncateMiddle(title, 140)}”` : 'Generated voiceover'
    }
    if (mode === 'generate_music') {
      return title ? `Generated music: “${truncateMiddle(title, 140)}”` : 'Generated music'
    }
    if (mode === 'translate') return 'Translated audio'
    return 'Transcribed audio'
  }

  // File write
  if (/fs_write_file/i.test(key)) {
    const record = coerceRecord(args)
    const path = record ? getString(record.path) : null
    if (path) return `Wrote file “${path}”`
    return 'Wrote file'
  }

  if (/Sidecar__delegate/i.test(key)) {
    if (title) return `Delegated to Sidecar: “${truncateMiddle(title, 140)}”`
    return 'Delegate to Sidecar'
  }

  // Image generation and edit
  if (/imageGenerationViaunifiedImage/i.test(key)) {
    if (title) return `Created image: “${truncateMiddle(title, 140)}”`
    return 'Creating image'
  }

  if (/imageEditViaunifiedImage/i.test(key)) {
    if (title) return `Edited image: “${truncateMiddle(title, 140)}”`
    return 'Editing image'
  }

  // --- 3. Fallback to static POJO label
  if (PLATFORM_TOOL_LABELS[key]) {
    return PLATFORM_TOOL_LABELS[key]
  }

  // --- 4. Fallback: make title-case from identifier
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
}
